/**
 * Dive state: the phase of the globe-to-twin transition, the antenna, asset
 * and site it is about, the fade layer (curtain) the overlay draws, what the
 * dish follows, and the last error. Written only by ./controller.ts; the
 * overlay, the Dive button and the twin HUD read it.
 *
 * Phases: idle, flying (globe camera to the site), fading (curtain, route
 * change, twin load and framing), twin (in the twin, pointing kept current),
 * returning (back to the globe).
 */
import { create } from 'zustand';
import type { PointingSource } from './pointing';

export type DivePhase = 'idle' | 'flying' | 'fading' | 'twin' | 'returning';

export interface DiveSite {
  antennaId: string;
  /** Antenna name for the caption (Wallops 26m). */
  name: string;
  /** Short name (WAL-26). */
  shortName: string;
  /** Station and country (Wallops Island, Virginia, USA). */
  place: string;
  lat: number;
  lon: number;
}

/** What the dish follows, refreshed with the pointing. */
export interface DiveTrack {
  source: PointingSource;
  missionId: string | null;
  /** Human-readable target for the caption and the HUD. */
  label: string;
  azimuthDeg: number;
  elevationDeg: number;
}

interface DiveState {
  phase: DivePhase;
  antennaId: string | null;
  assetId: string | null;
  site: DiveSite | null;
  error: string | null;
  /** True while the fade layer is opaque or fading to opaque. */
  curtain: boolean;
  /** Fade time of the current transition: 300 ms, 0 under reduced motion. */
  fadeMs: number;
  track: DiveTrack | null;

  begin: (antennaId: string, fadeMs: number) => void;
  setTarget: (assetId: string, site: DiveSite) => void;
  setPhase: (phase: DivePhase) => void;
  setCurtain: (curtain: boolean) => void;
  setTrack: (track: DiveTrack | null) => void;
  /** Back to idle; `error` records why when the dive or the return failed. */
  end: (error?: string | null) => void;
}

const IDLE = {
  phase: 'idle' as DivePhase,
  antennaId: null,
  assetId: null,
  site: null,
  curtain: false,
  track: null,
};

export const useDive = create<DiveState>()((set) => ({
  ...IDLE,
  error: null,
  fadeMs: 300,
  begin: (antennaId, fadeMs) => set({ ...IDLE, phase: 'flying', antennaId, fadeMs, error: null }),
  setTarget: (assetId, site) => set({ assetId, site }),
  setPhase: (phase) => set({ phase }),
  setCurtain: (curtain) => set({ curtain }),
  setTrack: (track) => set({ track }),
  end: (error = null) => set({ ...IDLE, error }),
}));
